import { Injectable } from '@angular/core';

import { Decimal } from 'decimal.js';

import { pickIdFactory } from '../../../core/picks/pick-id';
import { IFailedSlipPickResult, IFailedSlipResult, ISucceededSlipResult, SlipResultStatus } from '../../betplacement/models';
import { isPartialSlip, isSucceededSlip } from '../../betplacement/utils';
import { BetslipPickStorageLoader } from '../../picks/services/betslip-pick-storage-loader';
import { BetPlacementError, BetPlacementErrorSaved } from '../../validation/errors/bet-placement-error';
import { ResultError, ResultErrorSaved } from '../../validation/errors/result/result-error';
import ErrorsStateStorageService from '../../validation/services/errors-state-storage.service';
import { IBetslipResultState, IBetslipResultStateSaved, IFailedSlipPickResultSaved, ISucceededSlipResultSaved, defaultResultState } from '../state';

@Injectable({ providedIn: 'root' })
export default class ResultStateStorageService {
    constructor(
        private pickLoader: BetslipPickStorageLoader,
        private errorStorageService: ErrorsStateStorageService,
    ) {}

    toSavedState(state: IBetslipResultState): IBetslipResultStateSaved {
        return {
            ...state,
            result: {
                ...state.result,
                slips: state.result.slips.map((slip) => {
                    if (isSucceededSlip(slip)) {
                        if (isPartialSlip(slip)) {
                            return { ...slip };
                        }

                        return {
                            ...slip,
                            picks: (<ISucceededSlipResult>slip).picks.map((pick) => ({
                                ...pick,
                                pickId: pick.pickId.toString(),
                                clientPick: pick.clientPick?.toJSON(),
                            })),
                        };
                    } else {
                        const failedSlip = <IFailedSlipResult>slip;

                        return {
                            ...failedSlip,
                            picks: Object.entries(failedSlip.picks).reduce(
                                (aggr, [key, value]) => {
                                    aggr[key] = {
                                        ...value,
                                        clientPick: value.clientPick.toJSON(),
                                        errors: value.errors.map((e) => this.errorStorageService.toSavedState(e) as ResultErrorSaved),
                                    };

                                    return aggr;
                                },
                                {} as { [pickId: string]: IFailedSlipPickResultSaved },
                            ),
                            errors: failedSlip.errors.map((e) => this.errorStorageService.toSavedState(e) as BetPlacementErrorSaved),
                        };
                    }
                }),
            },
        };
    }

    fromSavedState(state: IBetslipResultStateSaved | undefined): IBetslipResultState {
        if (!state) {
            return defaultResultState;
        }

        return {
            ...state,
            result: {
                ...state.result,
                slips: state.result.slips.map((slip) => {
                    if (slip.status === SlipResultStatus.Success) {
                        const succeededSlip = <ISucceededSlipResultSaved>slip;

                        if (isPartialSlip(succeededSlip)) {
                            return { ...succeededSlip };
                        }

                        return {
                            ...succeededSlip,
                            picks: succeededSlip.picks.map((pick) => {
                                return {
                                    ...pick,
                                    pickId: pickIdFactory(pick.pickId),
                                    clientPick: pick.clientPick ? this.pickLoader.loadPick(pick.clientPick) : undefined,
                                };
                            }),
                        };
                    } else {
                        return {
                            ...slip,
                            picks: Object.entries(slip.picks).reduce(
                                (aggr, [key, value]) => {
                                    aggr[key] = {
                                        ...value,
                                        clientPick: this.pickLoader.loadPick(value.clientPick),
                                        errors: value.errors.map((e) => this.errorStorageService.fromSavedState(e) as ResultError),
                                    };

                                    return aggr;
                                },
                                {} as { [pickId: string]: IFailedSlipPickResult },
                            ),
                            errors: slip.errors.map((e) => this.errorStorageService.fromSavedState(e) as BetPlacementError),
                        };
                    }
                }),
            },
            summary: state.summary
                ? {
                      ...state.summary,
                      possibleWinnings: {
                          title: state.summary?.possibleWinnings?.title ?? '',
                          value: state.summary?.possibleWinnings?.value ? new Decimal(state.summary?.possibleWinnings?.value) : null,
                          boostedValue: state.summary?.possibleWinnings?.boostedValue,
                      },
                      taxation: state.summary.taxation
                          ? {
                                possibleWinningsGrossTitle: state.summary.taxation?.possibleWinningsGrossTitle ?? '',
                                possibleWinningsGross: state.summary.taxation?.possibleWinningsGross
                                    ? new Decimal(state.summary.taxation?.possibleWinningsGross)
                                    : null,
                                taxationValue: state.summary?.taxation?.taxationValue
                                    ? {
                                          amount: new Decimal(state.summary?.taxation?.taxationValue?.amount ?? 0),
                                          rate: state.summary?.taxation?.taxationValue?.rate
                                              ? new Decimal(state.summary?.taxation?.taxationValue?.rate)
                                              : undefined,
                                          liabilityAmount: state.summary?.taxation?.taxationValue?.liabilityAmount
                                              ? new Decimal(state.summary?.taxation?.taxationValue?.liabilityAmount)
                                              : undefined,
                                          isPlaceholderTax: state.summary.taxation.taxationValue.isPlaceholderTax,
                                      }
                                    : null,
                            }
                          : undefined,
                  }
                : null,
        };
    }
}
